import { useMemo } from 'react';
import { ColumnDef, Column, Row, Table } from '@tanstack/react-table';
import { CurrencyInput } from '@dealstack/frontend/app/pages/Valuations/components/CurrencyInput/CurrencyInput';
import { ExpandableCounterTagsCell } from '@dealstack/frontend/app/pages/Valuations/components/table/cells/ExpandableCounterTagsCell';
import { DeleteActionCell } from '@dealstack/frontend/app/pages/Valuations/components/table/cells/DeleteActionCell';
import { BaseValuationRow } from '@dealstack/frontend/app/pages/Valuations/components/table/types';
import { TableColumnBuilder } from '@dealstack/frontend/app/pages/Valuations/components/table/columnBuilder/columnBuilder';
import { ColumnBuilderConfig } from '@dealstack/frontend/app/pages/Valuations/components/table/columnBuilder/columnBuilder.types';
import { DefaultHeaderCell } from '@dealstack/frontend/app/pages/Valuations/components/table/cells/DefaultHeaderCell';
import { SourcesUsesUndistributedRowId } from '@dealstack/frontend/app/pages/Valuations/views/SourcesAndUses/shared/SourcesUsesTable';
import { Controller, useFormContext } from 'react-hook-form';
import { TextInput } from '@mantine/core';
import { VALUATION_ELEMENT_ID } from '@dealstack/shared/tests/testElementIds';
import { NewDefaultCell } from '@dealstack/frontend/app/pages/Valuations/components/table/cells/NewDefaultCell';

type Variant = 'inputs' | 'distributions' | 'proceeds';

interface BuildColumnsParams {
	variant: Variant;
	onDeleteRow?: (rowId: string) => void;
	onFieldChange?: (index: number, fieldPath: string, value: unknown) => void;
	onFieldBlur?: (index: number) => void;
}

interface Props {
	tanstackRow: Row<BaseValuationRow>;
	table: Table<BaseValuationRow>;
	column: Column<BaseValuationRow>;
	value: unknown;
	variant: Variant;
	isExpanded: boolean;
	onDeleteRow?: (rowId: string) => void;
	onFieldChange?: (index: number, fieldPath: string, value: unknown) => void;
	onFieldBlur?: (index: number) => void;
}

export function isReadOnlyRow(rowId: string): boolean {
	return (
		rowId.endsWith(SourcesUsesUndistributedRowId) ||
		rowId.includes('warrant-receivables') ||
		rowId.includes('warrant-payables')
	);
}

function NameCell({
	tanstackRow,
	table,
	column,
	value,
	variant,
	onFieldChange,
	onFieldBlur,
}: Props) {
	const formContext = useFormContext();
	const stringValue = String(value ?? '');
	const isFirstColumn = column.id === table.getVisibleLeafColumns()[0]?.id;
	const row = tanstackRow;
	const rowMeta = (row.original.metadata ?? {}) as NonNullable<BaseValuationRow['metadata']>;
	const isReadOnly = isReadOnlyRow(row.original.id);

	if (
		isFirstColumn &&
		rowMeta.securityPersonCount &&
		(rowMeta.type === 'issuer' || rowMeta.type === 'securityName')
	) {
		const phraseLabel =
			rowMeta.type === 'issuer'
				? 'sources_and_uses.security_tag'
				: 'sources_and_uses.person_tag';
		return (
			<ExpandableCounterTagsCell
				value={stringValue}
				count={rowMeta.securityPersonCount}
				phraseLabel={phraseLabel}
				row={row as unknown as Row<BaseValuationRow>}
				isExpanded={row.getIsExpanded()}
				italic={rowMeta.excludedFromTotal}
			/>
		);
	}

	const isEditableInput = variant === 'inputs';

	if (rowMeta.type === 'custom' && isEditableInput && !isReadOnly) {
		const idParts = row.original.id.split('-');
		const lastPart = idParts[idParts.length - 1] ?? '';
		let index = 0;
		if (lastPart.startsWith('custom')) {
			const parsed = parseInt(lastPart.replace('custom', ''), 10);
			index = Number.isNaN(parsed) ? 0 : parsed;
		}

		if (!formContext) {
			return (
				<NewDefaultCell
					value={stringValue}
					disableBorders={true}
					alignEnd={true}
					row={tanstackRow}
					padding="0"
				/>
			);
		}

		return (
			<Controller
				control={formContext.control}
				name={`inputs.${index}.name`}
				defaultValue={stringValue}
				render={({ field }) => (
					<TextInput
						data-testid={VALUATION_ELEMENT_ID.SOURCES_AND_USES_TABLE_NAME_CELL_INPUT}
						{...field}
						disabled={rowMeta.disabled}
						onChange={(e) => {
							const v = e.target.value;
							field.onChange(v);
							onFieldChange?.(index, 'name', v);
						}}
						styles={{
							input: {
								marginTop: '0',
							},
						}}
						onBlur={() => onFieldBlur?.(index)}
					/>
				)}
			/>
		);
	}

	const meta = (column.columnDef.meta as { alignment?: 'start' | 'end' }) ?? {};
	return (
		<NewDefaultCell
			value={stringValue}
			disableBorders={true}
			alignEnd={meta.alignment === 'end'}
			row={tanstackRow}
			padding="0"
			italic={rowMeta.excludedFromTotal}
		/>
	);
}

function InputAmountCell({ tanstackRow, value, variant, onFieldChange, onFieldBlur }: Props) {
	const row = tanstackRow;
	const rowMeta = (row.original.metadata ?? {}) as NonNullable<BaseValuationRow['metadata']>;
	const isReadOnly = isReadOnlyRow(row.original.id);
	const stringValue = String(value ?? '');

	if (variant === 'inputs' && rowMeta.type === 'custom' && !isReadOnly) {
		const idParts = row.original.id.split('-');
		const lastPart = idParts[idParts.length - 1] ?? '';
		let index = 0;
		if (lastPart.startsWith('custom')) {
			const parsed = parseInt(lastPart.replace('custom', ''), 10);
			index = Number.isNaN(parsed) ? 0 : parsed;
		}
		return (
			<CurrencyInput
				name={`inputs.${index}.value`}
				onChange={(v) => onFieldChange?.(index, 'value', v)}
				onBlur={() => onFieldBlur?.(index)}
				disabled={rowMeta.disabled}
				styles={{
					textInput: {
						input: {
							marginTop: '0',
						},
					},
				}}
			/>
		);
	}
	return (
		<NewDefaultCell
			value={stringValue}
			disableBorders={true}
			alignEnd={true}
			row={tanstackRow}
			padding="0"
			italic={rowMeta.excludedFromTotal}
		/>
	);
}

function ConvertedAmountCell({ value, tanstackRow }: Props) {
	const rowMeta = (tanstackRow.original.metadata ?? {}) as NonNullable<
		BaseValuationRow['metadata']
	>;
	const stringValue = String(value ?? '');
	return (
		<NewDefaultCell
			value={stringValue}
			disableBorders={true}
			alignEnd={true}
			row={tanstackRow}
			padding="0"
			italic={rowMeta.excludedFromTotal}
		/>
	);
}

function ActionsCell({ tanstackRow, onDeleteRow }: Props) {
	const row = tanstackRow;
	const rowMeta = (row.original.metadata ?? {}) as NonNullable<BaseValuationRow['metadata']>;
	if (row.original.values?.actions && rowMeta.type === 'custom' && onDeleteRow) {
		return (
			<DeleteActionCell
				rowId={row.original.id}
				onDelete={onDeleteRow}
				disabled={rowMeta.disabled}
			/>
		);
	}
	return (
		<NewDefaultCell
			value=""
			disableBorders={true}
			alignEnd={true}
			row={tanstackRow}
			padding="0"
		/>
	);
}

export function useSourcesUsesSecuritiesColumns({
	variant,
	onDeleteRow,
	onFieldChange,
	onFieldBlur,
}: BuildColumnsParams): ColumnDef<BaseValuationRow, unknown>[] {
	return useMemo(() => {
		const config: ColumnBuilderConfig<BaseValuationRow> = {
			showFilterInputs: false,
			components: {
				HeaderCell: DefaultHeaderCell,
				DefaultCell: NewDefaultCell,
			},
		};

		const builder = new TableColumnBuilder<BaseValuationRow>(config)
			.addColumnWithCustomCell<Props>({
				id: 'name',
				header: '',
				accessor: (row) => row.values?.name ?? '',
				alignment: 'start',
				cellComponent: NameCell,
				cellPropsMapper: ({ tanstackRow, table, column, value }) => ({
					tanstackRow,
					table,
					column,
					value,
					variant,
					onDeleteRow,
					onFieldChange,
					onFieldBlur,
					isExpanded: tanstackRow.getIsExpanded(),
				}),
			})
			.addColumnWithCustomCell<Props>({
				id: 'inputAmount',
				header: '',
				accessor: (row) => row.values?.inputAmount ?? '',
				alignment: 'end',
				cellComponent: InputAmountCell,
				cellPropsMapper: ({ tanstackRow, table, column, value }) => ({
					tanstackRow,
					table,
					column,
					value,
					variant,
					onDeleteRow,
					onFieldChange,
					onFieldBlur,
					isExpanded: tanstackRow.getIsExpanded(),
				}),
			})
			.addColumnWithCustomCell<Props>({
				id: 'convertedAmount',
				header: '',
				accessor: (row) => row.values?.convertedAmount ?? '',
				alignment: 'end',
				cellComponent: ConvertedAmountCell,
				cellPropsMapper: ({ tanstackRow, table, column, value }) => ({
					tanstackRow,
					table,
					column,
					value,
					variant,
					onDeleteRow,
					onFieldChange,
					onFieldBlur,
					isExpanded: tanstackRow.getIsExpanded(),
				}),
			})
			.addColumnWithCustomCell<Props>({
				id: 'actions',
				header: '',
				accessor: (row) => row.values?.actions ?? '',
				alignment: 'end',
				cellComponent: ActionsCell,
				cellPropsMapper: ({ tanstackRow, table, column, value }) => ({
					tanstackRow,
					table,
					column,
					value,
					variant,
					onDeleteRow,
					onFieldChange,
					onFieldBlur,
					isExpanded: tanstackRow.getIsExpanded(),
				}),
			});

		return builder.build();
	}, [variant, onDeleteRow, onFieldChange, onFieldBlur]);
}
